/**
 * The optional passphrase (decisions 11 and 48, #1695): a THIRD independent
 * wrapping of MK, beside the recovery code's.
 *
 * WHAT IT IS FOR, and what it is not. The recovery code covers the loss case
 * with 150 random bits this app chose. A passphrase is the memorable
 * alternative for the member who will not save a code — and it is WEAKER, by a
 * lot. That is not a caveat to bury: for the code the KDF is bookkeeping, for
 * the passphrase the KDF is the whole control, and the app says so where the
 * member chooses one.
 *
 * PBKDF2-SHA-256, NOT ARGON2ID (decision 48). Argon2 would put a second,
 * third-party cryptographic implementation on the only path that can hand a
 * member their notes back, and at an honest memory cost it is a new
 * allocation-failure lockout on exactly the phones `minSdk 28` admits —
 * indistinguishable, to the member, from a wrong passphrase. `kdf` and the
 * settings live in the wrapping, so adding Argon2id later is one branch here;
 * removing it after members hold argon2 wrappings is not.
 *
 * THE COST, STATED. At 600,000 iterations a single modern GPU manages on the
 * order of 10^4 guesses a second, so this buys roughly forty bits over the
 * passphrase's own entropy and no more. There is no rate limit on guessing and
 * there cannot be one: the wrapping is served on `GET /api/me/encryption` to
 * any signed-in session, so one authenticated response is all an attacker needs
 * before grinding offline for ever. The API must never grow an attempt counter
 * (api#1125 asserts it does not); the controls that work are a strength gate
 * where the member types it, and honest copy.
 *
 * FLAT, AND IT HAS TO BE. `canonicalJson` accepts scalars only and throws on a
 * nested object, so every KDF setting is a top-level field. The shape was first
 * written with the settings under `params`, which would have passed review and
 * every test that never hashed it, then failed at the first real `PUT` with a
 * signature error naming no field.
 */
import { hashCanonical } from './canonical';
import { base64ToBytes, bytesToBase64, utf8Encode } from './encoding';
import { assertKeyId, assertMemberId } from './identifiers';
import { MASTER_KEY_BYTES, assertMasterKey } from './master-key';
import { getSubtle, randomBytes } from './webcrypto';

/** The only KDF this build writes or reads. A whitelist, per decision 48. */
export const PASSPHRASE_KDF = 'PBKDF2-SHA-256';

/**
 * OWASP's figure for PBKDF2-HMAC-SHA256, and the floor whatever a device
 * measures. api#1125 refuses a wrapping below it, so a client that drifted
 * under would fail the `PUT` rather than quietly weaken the member.
 */
export const MIN_PASSPHRASE_ITERATIONS = 600_000;

const SALT_BYTES = 16;
const IV_BYTES = 12;
const WRAP_AAD_LABEL = 'hogc-one/courses/v1/passphrase';

export const PASSPHRASE_ERRORS = Object.freeze({
  WRONG_PASSPHRASE: 'WRONG_PASSPHRASE',
  MALFORMED_WRAPPING: 'MALFORMED_WRAPPING',
  UNSUPPORTED_KDF: 'UNSUPPORTED_KDF',
  WEAK_ITERATIONS: 'WEAK_ITERATIONS',
  EMPTY_PASSPHRASE: 'EMPTY_PASSPHRASE',
});

export class PassphraseError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'PassphraseError';
    this.code = code;
  }
}

/**
 * Bound to the member and the MK generation, exactly as the recovery wrapping
 * is. A wrapping lifted from another member, or from this member's previous
 * generation, fails the GCM tag rather than decrypting to the wrong key.
 *
 * @param {number} memberId
 * @param {string} keyId
 * @returns {Uint8Array}
 */
function wrapAad(memberId, keyId) {
  return utf8Encode(`${WRAP_AAD_LABEL}:${memberId}:${keyId}`);
}

/**
 * The passphrase as bytes.
 *
 * NFKC-normalised, because the same passphrase typed on two keyboards can be
 * two different byte strings — a composed é and a decomposed one look
 * identical and would be a wrong passphrase on the second device, which is the
 * device that matters. Whitespace is NOT trimmed beyond the ends: a member who
 * chose a phrase with spaces in it meant them.
 *
 * @param {string} passphrase
 * @returns {Uint8Array}
 */
function passphraseBytes(passphrase) {
  if (typeof passphrase !== 'string') {
    throw new PassphraseError(PASSPHRASE_ERRORS.EMPTY_PASSPHRASE, 'Enter a passphrase');
  }
  const normalised = passphrase.normalize('NFKC').trim();
  if (!normalised) {
    throw new PassphraseError(PASSPHRASE_ERRORS.EMPTY_PASSPHRASE, 'Enter a passphrase');
  }
  return utf8Encode(normalised);
}

/**
 * KEK_p = PBKDF2-SHA-256(passphrase, salt, iterations).
 *
 * @param {Uint8Array} bytes
 * @param {Uint8Array} salt
 * @param {number} iterations
 * @param {KeyUsage[]} usages
 * @returns {Promise<CryptoKey>}
 */
async function derivePassphraseKey(bytes, salt, iterations, usages) {
  const subtle = getSubtle();
  const base = await subtle.importKey('raw', bytes, 'PBKDF2', false, ['deriveKey']);
  return subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    usages,
  );
}

/**
 * @param {unknown} wrapping
 * @returns {{ salt: Uint8Array, iv: Uint8Array, wrapped: Uint8Array, iterations: number }}
 */
function decodeWrapping(wrapping) {
  const malformed = detail =>
    new PassphraseError(
      PASSPHRASE_ERRORS.MALFORMED_WRAPPING,
      `The saved passphrase wrapping is not valid: ${detail}`,
    );
  if (wrapping === null || typeof wrapping !== 'object') throw malformed('not an object');

  // An unknown KDF is not a wrong passphrase and must not be reported as one:
  // the member would keep retrying a phrase that is right. Section 6.3's
  // sentence shape for an unknown envelope `v` applies — this needs a newer
  // app version, and saying so is the only honest answer.
  if (wrapping.kdf !== PASSPHRASE_KDF) {
    throw new PassphraseError(
      PASSPHRASE_ERRORS.UNSUPPORTED_KDF,
      'This passphrase needs a newer app version',
    );
  }
  const { iterations } = wrapping;
  if (!Number.isInteger(iterations) || iterations < MIN_PASSPHRASE_ITERATIONS) {
    // Below the floor is not something to "try anyway": it is a wrapping this
    // build would refuse to write, and the API would refuse to store.
    throw new PassphraseError(
      PASSPHRASE_ERRORS.WEAK_ITERATIONS,
      'That passphrase wrapping was made with too few iterations',
    );
  }

  let salt;
  let iv;
  let wrapped;
  try {
    salt = base64ToBytes(wrapping.salt);
    iv = base64ToBytes(wrapping.iv);
    wrapped = base64ToBytes(wrapping.wrapped);
  } catch (error) {
    throw malformed('salt, iv or wrapped is not base64');
  }
  if (iv.length !== IV_BYTES) throw malformed('iv length');
  if (salt.length === 0) throw malformed('empty salt');
  return { salt, iv, wrapped, iterations };
}

/**
 * MK under a passphrase-derived KEK.
 *
 * @param {object} args
 * @param {Uint8Array} args.mk
 * @param {string} args.keyId MK's generation
 * @param {number} args.memberId
 * @param {string} args.passphrase as typed
 * @param {number} [args.iterations] measured on the device; never below the floor
 * @returns {Promise<{ kdf: string, iterations: number, salt: string, iv: string, wrapped: string }>}
 */
export async function wrapMasterKeyUnderPassphrase({
  mk,
  keyId,
  memberId,
  passphrase,
  iterations = MIN_PASSPHRASE_ITERATIONS,
}) {
  assertMasterKey(mk);
  assertKeyId(keyId);
  assertMemberId(memberId);
  // The floor wins over anything a caller measured. A device that timed itself
  // as slow does not get to make the member's passphrase easier to grind.
  const rounds = Math.max(Math.trunc(iterations) || 0, MIN_PASSPHRASE_ITERATIONS);

  const bytes = passphraseBytes(passphrase);
  const salt = randomBytes(SALT_BYTES);
  const key = await derivePassphraseKey(bytes, salt, rounds, ['encrypt']);
  const iv = randomBytes(IV_BYTES);
  const wrapped = await getSubtle().encrypt(
    { name: 'AES-GCM', iv, additionalData: wrapAad(memberId, keyId) },
    key,
    mk,
  );

  return {
    kdf: PASSPHRASE_KDF,
    iterations: rounds,
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    wrapped: bytesToBase64(new Uint8Array(wrapped)),
  };
}

/**
 * MK back out, or WRONG_PASSPHRASE.
 *
 * `iterations` is read from the WRAPPING, never from this build's constant, so
 * raising the floor later does not strand a wrapping made under the old one.
 *
 * @param {object} args
 * @param {unknown} args.wrapping `passphraseWrapping` from the status response
 * @param {string} args.keyId the bundle's `keyId` from the same response
 * @param {number} args.memberId
 * @param {string} args.passphrase as typed
 * @returns {Promise<Uint8Array>} MK
 */
export async function unwrapMasterKeyWithPassphrase({ wrapping, keyId, memberId, passphrase }) {
  assertKeyId(keyId);
  assertMemberId(memberId);
  const { salt, iv, wrapped, iterations } = decodeWrapping(wrapping);
  const bytes = passphraseBytes(passphrase);
  const key = await derivePassphraseKey(bytes, salt, iterations, ['decrypt']);

  let mk;
  try {
    mk = new Uint8Array(
      await getSubtle().decrypt(
        { name: 'AES-GCM', iv, additionalData: wrapAad(memberId, keyId) },
        key,
        wrapped,
      ),
    );
  } catch (error) {
    // The GCM tag is the whole check: nothing is stored to compare against, so
    // a wrong passphrase, a wrapping from another member and one from another
    // generation all land here. The member only ever caused the first.
    throw new PassphraseError(
      PASSPHRASE_ERRORS.WRONG_PASSPHRASE,
      'That passphrase does not match. Check it and try again',
    );
  }
  if (mk.length !== MASTER_KEY_BYTES) {
    throw new PassphraseError(PASSPHRASE_ERRORS.WRONG_PASSPHRASE, 'That passphrase does not match');
  }
  return mk;
}

/**
 * The `passphraseWrappingHash` a `passphrase` signature carries (API contract
 * section 5, api#1125).
 *
 * Destructured rather than spread: the server hashes the DTO's declared fields,
 * so a caller passing an object with anything extra on it must not change the
 * hash here. A mismatch is a 403 with nothing in it to say which field was
 * wrong.
 *
 * @param {{ kdf: string, iterations: number, salt: string, iv: string, wrapped: string }} wrapping
 * @returns {Promise<string>}
 */
export function passphraseWrappingHash({ kdf, iterations, salt, iv, wrapped }) {
  return hashCanonical({ kdf, iterations, salt, iv, wrapped });
}
